import { useEffect } from "react";
import { useSharingFeature } from "../useSharingFeature";
import { useUIStore } from "../../store/useUIStore";
import { useGroupListStore } from "../../store/useGroupListStore";

/**
 * useShareView
 *
 * 공유하기 모달 로직을 관리하는 Presentation Hook
 *
 * 기능:
 * - 모달 열릴 때 자동으로 공유 URL 생성
 * - 클립보드 복사 처리
 * - 로딩 및 에러 상태 관리
 */
const useShareView = () => {
  const { currentGroupId } = useGroupListStore();
  const { shareModalOpen, setShareModalOpen } = useUIStore();

  const {
    uuid,
    loading,
    error,
    copySuccess,
    createShareUrl,
    copyToClipboard,
    getShareUrl,
    reset,
  } = useSharingFeature();

  // 모달이 열릴 때 자동으로 공유 URL 생성
  useEffect(() => {
    if (shareModalOpen && currentGroupId && !uuid && !loading) {
      createShareUrl("group_id", parseInt(currentGroupId));
    }
  }, [shareModalOpen, currentGroupId, uuid, loading, createShareUrl]);

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!shareModalOpen) {
      reset();
    }
  }, [shareModalOpen, reset]);

  // 모달 열기/닫기 핸들러
  const handleOpenChange = (open: boolean) => {
    setShareModalOpen(open);
  };

  // 복사 버튼 핸들러
  const handleCopy = () => {
    const shareUrl = getShareUrl();
    if (shareUrl) {
      copyToClipboard(shareUrl);
    }
  };

  return {
    // 모달 상태
    shareModalOpen,
    handleOpenChange,

    // 공유 URL 데이터
    shareUrl: getShareUrl(),
    loading,
    error,
    copySuccess,

    // 핸들러
    onCopy: handleCopy,
  };
};

export default useShareView;
